import type { Dispatch, SetStateAction } from "react";
import { useState } from "react";

/**
 * Local state seeded from `derivedValue` that resets whenever `derivedValue`
 * changes, while still allowing independent local updates between those changes
 * (e.g. a debounced input whose baseline comes from a prop or URL param).
 *
 * Implements React's "adjusting state when a prop changes" pattern by comparing
 * against the previous value and resetting during render — not in a `useEffect`.
 * This avoids the extra render an effect-based sync causes and needs no
 * `react-hooks/set-state-in-effect` escape hatch.
 * https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
 */
export function useDerivedState<T>(
  derivedValue: T,
): [T, Dispatch<SetStateAction<T>>] {
  // Wrap in initializers/updaters so a function-typed `T` is stored as-is
  // rather than being invoked as a lazy initializer or state updater.
  const [value, setValue] = useState<T>(() => derivedValue);
  const [previousDerivedValue, setPreviousDerivedValue] = useState<T>(
    () => derivedValue,
  );

  if (!Object.is(derivedValue, previousDerivedValue)) {
    setPreviousDerivedValue(() => derivedValue);
    setValue(() => derivedValue);
  }

  return [value, setValue];
}
